import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from 'components/ui-components/Button';
import TextInput from 'components/ui-components/TextInput';
import Card from 'components/ui-components/Card';
import { useHTTP } from 'hooks/useRequest';
import { isValidMobileNumber } from 'utils/validation';
import responsive from 'utils/responsive';
import { gaMFAError } from '../../../../data_layer/ga_authentication_events';

const MFAChangeMobilePhoneForm = ({
  existingMobileNumber,
  mfaEnabled,
  mfaParam,
}) => {
  const [mobilePhoneNumber, setMobilePhoneNumber] =
    useState(existingMobileNumber);
  const [error, setError] = useState({});
  const [validateErrors, setValidateErrors] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [hasBeenCancelled, setHasBeenCancelled] = useState(false);

  const mobileUpdateRequestConfig = {
    method: 'PUT',
    url: hasBeenSubmitted ? '/mycef/mobile_number' : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      mobile_number_update: { mobile_number: mobilePhoneNumber },
      mfa: mfaParam,
    }),
  };

  const {
    data: mobileUpdateResponse,
    error: mobileUpdateError,
    loading,
  } = useHTTP(mobileUpdateRequestConfig || null);

  const cancelRequestConfig = {
    method: 'POST',
    url: hasBeenCancelled ? '/mfa/cancel' : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const { data: cancelData } = useHTTP(cancelRequestConfig || null);

  const validateMobilePhoneNumber = number => {
    const { success, error: phoneError } = isValidMobileNumber(number);

    setError(current => ({
      ...current,
      mobileNumber: phoneError ? { message: phoneError } : undefined,
    }));

    return success;
  };

  const handleMobileChange = e => {
    setMobilePhoneNumber(e.target.value);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setValidateErrors(true);
    const mobileNumberSuccess = validateMobilePhoneNumber(mobilePhoneNumber);

    if (mobileNumberSuccess) {
      setHasBeenSubmitted(true);
    }
  };

  useEffect(() => {
    setHasBeenSubmitted(false);

    const errors = mobileUpdateError?.error?.data?.errors;

    if (mobileUpdateResponse?.success) {
      window.location.href = mobileUpdateResponse?.url;
    } else if (errors !== undefined) {
      gaMFAError(errors);
      setError({
        mobileNumber: {
          message: Object.values(errors)[0],
        },
      });
    }
  }, [mobileUpdateResponse, mobileUpdateError]);

  useEffect(() => {
    setHasBeenCancelled(false);

    if (cancelData?.success && cancelData?.url) {
      window.location.href = cancelData.url;
    }
  }, [cancelData]);

  useEffect(() => {
    if (validateErrors) {
      validateMobilePhoneNumber(mobilePhoneNumber);
    }
  }, [mobilePhoneNumber, validateErrors]);

  return (
    <FormWrapper className="margin-center width-100">
      <h1
        className={`ff-body fw-600 fs-30 l-fs-36 lh-1-2 l-lh-1 tc-main ta-c mt-52 ${
          mfaEnabled ? 'mb-12' : 'mb-32'
        }`}>
        {mfaParam ? 'Two-Factor Authentication' : 'Change Mobile Number'}
      </h1>
      {mfaEnabled && (
        <p className="fs-18 lh-1-25 tc-secondary ta-c m-fb-60 pb-28">
          {mfaParam
            ? 'Protect your account by setting up two-factor authentication. A verification code will be required in addition to your password when you sign in.'
            : 'Changing your mobile number will also update your two-factor authentication contact number.'}
        </p>
      )}
      <form onSubmit={handleSubmit}>
        <Card variant="large" className="mb-32">
          <div className="mb-24">
            {mfaParam && (
              <>
                <h3 className="ff-body fs-21 m-fs-24 lh-1-1 tc-main mt-0 mb-24">
                  Confirm Mobile Number
                </h3>
                <p className="tc-secondary fs-16 lh-1-3 mb-45">
                  A one-time code will be sent by text message to this number
                  each time you sign in to your account.
                </p>
              </>
            )}

            <TextInput
              variant="mobile"
              type="tel"
              id="mobileNumber"
              value={mobilePhoneNumber}
              onChange={handleMobileChange}
              required
              label="Mobile Number"
              description={
                !mfaEnabled
                  ? ''
                  : mfaParam
                  ? 'Changing this number will update the mobile number on your account'
                  : 'A verification code will be sent to this number'
              }
              errors={error && error.mobileNumber ? error : null}
              className="mb-45"
            />
          </div>

          <div className="actions fl sm-fl-col m-fl-row-reverse jc-sb ai-c">
            <SubmitButton
              type="submit"
              className="cef-btn cef-btn--green cef-btn--feature m-ml-24 width-100">
              {loading ? (
                <span className="cef-btn__loader bs-bb" />
              ) : mfaParam ? (
                'Send code'
              ) : (
                'Continue'
              )}
            </SubmitButton>

            <Button
              className="fl ai-c td-u fs-18 tc-grey-60 cef-btn--high cef-btn--as-link mt-24 m-mt-0"
              handleClick={() => setHasBeenCancelled(true)}>
              Cancel
            </Button>
          </div>
        </Card>
      </form>
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  ${responsive('desktopSmall')} {
    max-width: 650px;
  }
`;

const SubmitButton = styled(Button)`
  max-width: 250px;
`;

export default MFAChangeMobilePhoneForm;
